/**
 * Quiz « Trouver mon parfum ».
 * Les parfums sont chargés à la première ouverture (section quiz-data), puis notés
 * selon les réponses : genre, familles, occasions, profil. Aucun e-mail n'est requis.
 */
class PerfumeQuiz extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-quiz-dialog]');
    this.form = this.querySelector('[data-quiz-form]');
    this.steps = [...this.querySelectorAll('[data-step]')];
    this.results = this.querySelector('[data-quiz-results]');
    this.nextButton = this.querySelector('[data-quiz-next]');
    this.backButton = this.querySelector('[data-quiz-back]');
    this.progress = this.querySelector('[data-quiz-progress]');
    this.current = 0;
    this.strings = {
      next: this.nextButton.textContent.trim(),
      see: this.dataset.seeResults,
    };

    // Libellé affiché (traduit) de chaque réponse, à partir de sa valeur interne
    this.optionLabels = Object.fromEntries(
      [...this.form.querySelectorAll('input[value]')].map((input) => [input.value, input.closest('label')?.querySelector('.quiz__option-label')?.textContent.trim()])
    );

    this.querySelector('[data-quiz-open]').addEventListener('click', () => this.open());

    // Tout lien vers « ?quiz=1 » ou « #quiz » ouvre le quiz (utilisable dans l'éditeur de thème)
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href*="quiz=1"], a[href$="#quiz"]');
      if (!link) return;
      event.preventDefault();
      this.open();
    });
    if (/[?&]quiz=1/.test(window.location.search) || window.location.hash === '#quiz') this.open();

    // Le bouton se replie en pastille dès qu'on fait défiler (toujours replié sur mobile)
    const launcher = this.querySelector('[data-quiz-open]');
    const compact = () => launcher.classList.toggle('is-compact', window.scrollY > 300 || window.innerWidth < 750);
    window.addEventListener('scroll', compact, { passive: true });
    window.addEventListener('resize', compact);
    compact();
    this.querySelectorAll('[data-quiz-close]').forEach((btn) => btn.addEventListener('click', () => this.dialog.close()));
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close(); // clic sur le fond
    });
    this.dialog.addEventListener('close', () => window.themeLockScroll?.(false));

    this.nextButton.addEventListener('click', () => this.next());
    this.backButton.addEventListener('click', () => this.go(this.current - 1));
    this.querySelector('[data-quiz-restart]').addEventListener('click', () => this.restart());

    // Étape 1 (choix unique) : on avance automatiquement
    this.steps[0].addEventListener('change', () => setTimeout(() => this.next(), 250));

    this.querySelector('[data-quiz-email]')?.addEventListener('submit', (event) => this.submitEmail(event));
  }

  open() {
    this.dialog.showModal();
    window.themeLockScroll?.(true);
    this.loadProducts();
  }

  loadProducts() {
    if (!this.productsPromise) {
      this.productsPromise = fetch(`${window.theme.routes.root}?section_id=quiz-data`)
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          return JSON.parse(doc.querySelector('[data-quiz-products]').textContent);
        })
        .catch(() => {
          this.productsPromise = null;
          return [];
        });
    }
    return this.productsPromise;
  }

  go(index) {
    this.current = Math.max(0, Math.min(index, this.steps.length - 1));
    this.steps.forEach((step, i) => {
      step.hidden = i !== this.current;
    });
    this.results.hidden = true;
    this.form.hidden = false;
    this.backButton.hidden = this.current === 0;
    this.nextButton.textContent = this.current === this.steps.length - 1 ? this.strings.see : this.strings.next;
    this.progress.style.setProperty('--progress', `${((this.current + 1) / (this.steps.length + 1)) * 100}%`);
    this.steps[this.current].querySelector('input')?.focus({ preventScroll: true });
  }

  next() {
    if (this.current < this.steps.length - 1) this.go(this.current + 1);
    else this.showResults();
  }

  restart() {
    this.form.reset();
    this.go(0);
  }

  answers() {
    const data = new FormData(this.form);
    return {
      genre: data.get('genre') || '',
      familles: data.getAll('familles'),
      occasions: data.getAll('occasions'),
      profil: data.getAll('profil'),
    };
  }

  score(product, answers) {
    let score = 0;
    let max = 0;
    const reasons = [];
    const overlap = (a, b) => a.filter((value) => b.includes(value));

    if (answers.genre) {
      max += 3;
      if (product.genre === answers.genre) score += 3;
      else if (product.genre === 'Mixte' || !product.genre) score += 2;
      else score -= 3; // genre explicitement différent : fortement pénalisé
    }

    if (answers.familles.length) {
      max += 4;
      const common = overlap(answers.familles, product.families);
      if (common.length) {
        score += Math.min(4, 2 + common.length * 1.5);
        reasons.push(...common);
      }
    }

    if (answers.occasions.length) {
      max += 2;
      const common = overlap(answers.occasions, product.occasions);
      score += Math.min(2, common.length);
      reasons.push(...common);
    }

    if (answers.profil.length) {
      max += 3;
      const common = overlap(answers.profil, product.profile);
      score += Math.min(3, common.length * 1.5);
      reasons.push(...common);
    }

    const match = max ? Math.max(0, Math.round((score / max) * 100)) : 50;
    // Les valeurs comparées restent en français (métachamps) ; on affiche le libellé de la langue du site
    const label = (value) => this.optionLabels?.[value] || value;
    return { product, score, match, reasons: [...new Set(reasons)].slice(0, 3).map(label) };
  }

  async showResults() {
    this.nextButton.setAttribute('aria-busy', 'true');
    const products = await this.loadProducts();
    this.nextButton.removeAttribute('aria-busy');
    const answers = this.answers();
    const limit = Number(this.dataset.resultsCount) || 3;

    const ranked = products
      .filter((p) => p.available)
      .map((p) => this.score(p, answers))
      .filter((r) => r.score > 0 || (!answers.familles.length && !answers.profil.length && !answers.occasions.length))
      .sort((a, b) => b.score - a.score)
      .slice(0, limit);

    const list = this.results.querySelector('[data-results-list]');
    list.innerHTML = ranked.map((r, i) => this.card(r, i)).join('');
    this.results.querySelector('[data-results-empty]').hidden = ranked.length > 0;

    const tags = this.results.querySelector('[data-quiz-tags]');
    if (tags) {
      const extra = [...answers.familles, ...answers.profil].map((v) => `quiz-${v}`).join(',');
      tags.value = ['newsletter', 'quiz', extra].filter(Boolean).join(',');
    }

    this.form.hidden = true;
    this.results.hidden = false;
    this.progress.style.setProperty('--progress', '100%');
    this.results.querySelector('[data-results-title]').focus({ preventScroll: true });
    this.dialog.querySelector('.quiz__panel').scrollTop = 0;
  }

  card({ product, match, reasons }, index) {
    const escape = (s) => String(s).replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
    const image = product.image
      ? `<img src="${escape(product.image)}" alt="" loading="lazy" width="500" height="625">`
      : this.querySelector('[data-quiz-placeholder]')?.innerHTML || '';
    return `
      <li class="quiz-result" style="--i:${index}">
        <span class="quiz-result__match">${match}&nbsp;%</span>
        <div class="quiz-result__media media">${image}</div>
        <p class="quiz-result__title"><a href="${escape(product.url)}">${escape(product.title)}</a></p>
        ${reasons.length ? `<p class="quiz-result__reasons">${reasons.map(escape).join(' · ')}</p>` : ''}
        <p class="quiz-result__price">${escape(product.price)}</p>
      </li>`;
  }

  async submitEmail(event) {
    event.preventDefault();
    const form = event.target;
    const message = form.querySelector('[data-quiz-email-message]');
    try {
      const response = await fetch(form.action, { method: 'POST', body: new FormData(form) });
      if (response.url.includes('challenge')) {
        window.location.href = response.url; // vérification anti-robot de Shopify
        return;
      }
      message.textContent = this.dataset.emailSuccess;
      form.querySelector('.quiz__email-field').hidden = true;
    } catch (error) {
      message.textContent = window.theme.strings.error;
    }
    message.hidden = false;
  }
}
customElements.define('perfume-quiz', PerfumeQuiz);
